import "./RangePocketLanding.css";
import React, { useState, useEffect, useRef } from "react";
import TrustBand from "../../../components/TrustBand/TrustBand";

/** Reusable detail modal (scrolls internally, page stays locked) */
function DetailModal({ open, onClose, item }) {
  // Lock body scroll + close on ESC
  useEffect(() => {
    if (!open) return;

    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset || html.scrollTop || 0;

    // Save previous inline styles so we can restore exactly
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
    };

    // Compensate scrollbar to avoid layout shift (desktop)
    const sbw = window.innerWidth - html.clientWidth;
    if (sbw > 0) body.style.paddingRight = `${sbw}px`;

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";

    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      body.style.position = prev.position || "";
      body.style.top = prev.top || "";
      body.style.width = prev.width || "";
      body.style.overflow = prev.overflow || "";
      body.style.paddingRight = prev.paddingRight || "";
      // restore scroll
      window.scrollTo(0, scrollY);
    };
  }, [open, onClose]);

  if (!open || !item) return null;

  // Prefer richer modal content if provided
  const images = item.modalImages?.length
    ? item.modalImages
    : [{ src: item.img, alt: item.alt }];
  const lead = item.modalLead || item.detail || item.blurb;

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
    >
      <div className="detail-dialog">
        <button className="detail-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {/* Header stays fixed; content below scrolls */}
        <header className="detail-header">
          <h2 className="detail-title">{item.title}</h2>
          {lead && <p className="detail-lead">{lead}</p>}
        </header>

        {/* Scrollable area */}
        <div className="detail-content">
          {/* Image + text pairs for ALL images (alternating left/right) */}
          {images.map((img, idx) => {
            const side = idx % 2 === 0 ? "image-left" : "image-right";
            const slideNote =
              img.body ?? img.caption ?? item.modalNotes ?? item.note ?? "";

            return (
              <section className={`detail-grid ${side}`} key={idx}>
                <div className="detail-media">
                  <div className="detail-shot-box">
                    <img
                      src={img.src}
                      alt={img.alt || `${item.title} ${idx + 1}`}
                    />
                  </div>
                </div>

                <div className="detail-copy">
                  {slideNote && <p className="detail-points">{slideNote}</p>}
                </div>
              </section>
            );
          })}

          {/* Optional long-form content blocks */}
          {Array.isArray(item.modalSections) &&
            item.modalSections.map((sec, i) => (
              <section className="detail-text-block" key={i}>
                {sec.heading && <h3>{sec.heading}</h3>}
                {sec.body && <p>{sec.body}</p>}
                {Array.isArray(sec.list) && (
                  <ul>
                    {sec.list.map((li, k) => (
                      <li key={k}>{li}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
        </div>
      </div>
    </div>
  );
}

export default function RangePocketLanding() {
  const IOS_URL = "https://apps.apple.com/us/app/range-pocket/id6733222657";
  const ANDROID_URL =
    "https://play.google.com/store/apps/details?id=com.armorysync.rangepocket";

  // "ios" | "android" | "all"
  const [preferredStore, setPreferredStore] = useState("all");
  const featureTrackRef = useRef(null);
  const featureDragRef = useRef({
    active: false,
    moved: false,
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
  });

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);

    if (isAndroid) setPreferredStore("android");
    else if (isIOS) setPreferredStore("ios");
    else setPreferredStore("all");
  }, []);

  useEffect(() => {
    if (!window.location.hash) return;
    const target = document.getElementById(window.location.hash.slice(1));
    if (!target) return;
    requestAnimationFrame(() => {
      if (window.location.hash === "#features" && featureTrackRef.current) {
        featureTrackRef.current.scrollLeft = 0;
      }
      target.scrollIntoView({ block: "start" });
    });
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (featureTrackRef.current) {
        featureTrackRef.current.scrollLeft = 0;
      }
    });
  }, []);

  const features = [
    {
      title: "Range Visits",
      blurb: "Log sessions with firearms, round counts, notes, and photos.",
      img: "/features/feature-range-visits.webp",
      alt: "Range visit list",
      align: "left",

      modalLead:
        "Capture every detail of your time at the range, organized and easy to review later.",
      modalPoints: [
        "Quick logging with smart defaults",
        "Track distance, wind, and group sizes when useful",
        "Attach targets, photos, and personal notes",
        "View visit history with maps and stats",
      ],
      modalImages: [
        {
          src: "/visited_alarm_setup.png",
          alt: "Visit detail overview",
      caption: `With Range Pocket, logging and managing your shooting experiences becomes effortless. 
        Every visit is organized with the key details that matter most: the range name, 
        the exact date and time, and the duration of your session, all in one clean, intuitive view.

        And it does not stop there. Keep your next sessions on track by setting alarms for upcoming visits,
        ensuring you never miss a session. Whether you are tracking past performance or planning ahead, 
        Range Pocket helps you stay focused on what really matters: improving your skills at the range.`,
        },
        {
          src: "/range_saved_ranges_example.png",
          alt: "Saved range",
          caption: `With Range Pocket, planning your sessions starts with your most trusted ranges. 
            Save the locations you visit often and instantly recall them with all the details that matter: 
            the map, name, address, phone number, and website from Maps 
            so you spend less time typing.

            When it is time to log or schedule a new visit, selecting from your saved ranges is effortless. 
            No need to re-enter information; just tap and go. By keeping your most important ranges a single step away, 
            Range Pocket makes every session smoother, more organized, and focused on what really matters: your time on the line.
            `,
        },
        {
          src: "/range_finder_map.png",
          alt: "Visit map & weather",
          caption: `Whether you are visiting a new city or just looking to explore spots nearby, 
            Range Pocket makes finding a range effortless. With the built-in map search, 
            you can instantly see all available ranges around you: clear, visual, and right 
            where you need them.

            And it does not stop there. With a single tap, the quick-access button highlights 
            nearby ranges, saving you the hassle of endless searches or manual entry. 
            From there, selecting a range is seamless whether you are planning your next visit or 
            logging a past session, the entire process stays fluid and intuitive.`,
        },
        {
          src: "/check_list.png",
          alt: "Checklist",
          caption: `With Range Pocket, you will never forget your firearms or gear. 
          Easily plan and digitalize your range visits by adding firearms and accessories directly 
          from your inventory to a checklist, making preparation effortless and organized.`,
        },
        {
          src: "/range_log.png",
          alt: "Range Log",
          caption: `Capturing the essentials is effortless: log your grouping, 
          offsets, distance, and wind conditions with just a few taps. 
          Add target photos and notes in seconds, creating a complete picture of your performance. 
          Designed to minimize effort and maximize clarity, Range Pocket turns your range log into 
          a powerful tool for growth, not just a record of the past.
          
          With Range Pocket, your range log is no longer limited to pen and paper. 
          Every detail of your shooting sessions is captured digitally, 
          giving you an unlimited record that grows with you. 
          Link each log directly to the firearms in your inventory with a single tap, 
          and connect multiple sessions together for deeper insights and trend analysis over time.
          `,
        },
      ],
    },
    {
      title: "Inventory",
      blurb: "Track firearms, ammo, accessories, and value in one place.",
      img: "/features/feature-firearm-list.webp",
      alt: "Inventory",
      align: "right",

      modalLead: "Your collection, organized and always accessible.",
      modalPoints: [
        "Log firearms with make, model, caliber, and photos",
        "Track ammo counts, purchases, and usage",
        "Manage accessories and link them to firearms",
        "See total collection value at a glance",
      ],
      modalImages: [
        {
          src: "/firearm_list.png",
          alt: "Firearm List",
          caption: `With images of your firearms displayed alongside key details like purchase date and 
          price, the inventory view brings all essential information together in one place. 
          And with a simple swipe, you can mark any firearm as a favorite, making it effortless 
          to manage and highlight the pieces you value most.`,
        },
        {
          src: "/firearm_detail.png",
          alt: "Firearm Detail",
          caption: `Still tracking your firearms in Excel or even on paper? 
          Range Pocket makes it simple. Log your collection in detail: price, brand, model, 
          acquisition method, source, serial number, and current location, all in one clean, 
          organized place.
          
          Beyond inventory, Range Pocket helps you stay prepared. 
          Keep receipts and purchase records with the firearm context you may need for insurance, 
          resale, or factory recall notes. Everything stays easier to find when you need it.`,
        },
        {
          src: "/firearm_shot_rounds.png",
          alt: "Firearm Shot History",
          caption: `With Range Pocket, every range visit is more than just a note. It is a 
          complete record. Each session you log stays grouped with 
          the firearms you used, building a detailed shooting history over time.
          
          Easily see how often you have taken a specific firearm to the range or how many 
          rounds you have fired through it this year. Range Pocket keeps your shooting data 
          organized, accessible, and ready whenever you need it.`,
        },
        {
          src: "/accessory_battery_life.png",
          alt: "Accessory Battery Alarm",
          caption: `Running out of power at the range is more than inconvenient. It can ruin a 
          session. Whether it is your electronic headset or critical accessories like a red dot 
          sight, Range Pocket helps you stay ahead by tracking battery life directly within your 
          inventory.

          Just set the expected battery lifespan and log the last replacement date, 
          and Range Pocket will project the next expected change. With one tap, you can even set 
          an alarm to remind you before it is too late. It gives you one more practical check 
          before the next range session.`,
        },
        {
          src: "/profile_showcase.png",
          alt: "Profile Showcase",
          caption: `For shooters who love to tinker and personalize, 
          Range Pocket makes customization seamless. Create a dedicated gunsmith profile that 
          brings every detail together: your base firearm, accessories, and individual parts, linked 
          in one place.
          
          With just a few taps, you can assemble, track, and evolve your custom builds without 
          spreadsheets or scattered notes. Whether you are experimenting with new optics, upgrading
           triggers, or designing a full custom setup, Range Pocket turns your vision into a digital 
           record you can refine and revisit anytime.`,
        },
      ],
    },
    {
      title: "Training",
      blurb: "Build plans, track drills, and measure progress over time.",
      img: "/features/feature-training-overview.webp",
      alt: "Training Hub",
      align: "left",

      modalLead:
        "Stay consistent, train smarter, and see your growth at a glance.",
      modalPoints: [
        "Create custom training plans tailored to your goals",
        "Log drills with distance, rounds, and conditions",
        "Organize by categories for structured practice",
        "Track statistics across sessions to measure progress",
      ],
      modalImages: [
        {
          src: "/custom_drill.png",
          alt: "Custom Drill",
          caption: `With Range Pocket, training is no longer limited to static templates. 
          Add any drill you want to practice, organize them into personalized categories, 
          and capture key notes or instructions. Mark your favorites for quick access, 
          making every session efficient and easier to manage.

          Go beyond single drills by combining them into structured training plans. 
          Each plan can consist of multiple custom drills, giving you the flexibility to 
          design sessions around your exact goals, whether it is mastering accuracy, building 
          speed, or preparing for a structured practice goal. Plans can be reused, adjusted, and tracked over
          time, ensuring your training stays focused and results-driven.`,
        },
        {
          src: "/training_plan_checklist.png",
          alt: "Training Plan Checklist",
          caption: `In Range Pocket, training plans are not just static lists. They are designed 
          to be actionable. You can attach multiple training plans to a single range visit, 
          giving you flexibility to structure each session around your goals. From accuracy drills to 
          speed exercises, everything you need is neatly organized and always ready when you are.

          And when you are on the range, Range Pocket helps you execute with clarity. Open any plan to 
          review its drills in detail, check them off as you complete each rep, and track your progress 
          in real time. By making your training measurable and structured, Range Pocket ensures that every visit translates into meaningful growth.`,
        },
        {
          src: "/calendar.png",
          alt: "calendar",
          caption: `Organization is built in. With Range Pocket’s calendar view, every range visit, 
          maintenance, and modification records are neatly displayed by month. 
          At a glance, you see your complete activity history, giving structure and clarity to your 
          shooting journey.
          
          Tap any date, and the calendar expands into a detailed day view: rounds fired, 
          time spent, equipment updates, all in one place. It is more than a log; it is a visual 
          timeline of your progress, making it effortless to stay consistent and on top of your 
          training.`,
        },
      ],
    },
  ];

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const openDetail = (item) => {
    setCurrent(item);
    setOpen(true);
  };

  const handleFeaturePointerDown = (event) => {
    if (event.button && event.button !== 0) return;
    const track = featureTrackRef.current;
    if (!track) return;

    featureDragRef.current = {
      active: true,
      moved: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: track.scrollLeft,
    };
    track.setPointerCapture?.(event.pointerId);
  };

  const handleFeaturePointerMove = (event) => {
    const drag = featureDragRef.current;
    const track = featureTrackRef.current;
    if (!drag.active || !track) return;

    const deltaX = event.clientX - drag.startX;
    if (Math.abs(deltaX) > 5) {
      drag.moved = true;
    }
    track.scrollLeft = drag.startScrollLeft - deltaX;
  };

  const endFeatureDrag = () => {
    const drag = featureDragRef.current;
    if (drag.pointerId != null) {
      featureTrackRef.current?.releasePointerCapture?.(drag.pointerId);
    }
    drag.active = false;
    drag.pointerId = null;
  };

  const openFeatureDetail = (event, item) => {
    if (featureDragRef.current.moved) {
      event.preventDefault();
      event.stopPropagation();
      featureDragRef.current.moved = false;
      return;
    }
    openDetail(item);
  };

  return (
    <main className="rp-page">
      {/* HERO */}
      <section className="rp-hero">
        <div className="rp-hero-inner">
          <div className="rp-hero-copy">
            <div className="rp-section-kicker">Range Pocket</div>
            <h1 className="rp-title">Your range history, inventory, and training in one organized app.</h1>
            <p className="rp-sub">
              Plan sessions, track firearms, log range visits, manage maintenance,
              and keep useful records easy to review.
            </p>
            <div className="rp-cta">
              {preferredStore === "android" ? (
                <>
                  <a className="rp-btn rp-btn--gold" href={ANDROID_URL}>
                    Get it on Google Play
                  </a>
                  <a className="rp-btn rp-btn--ghost" href={IOS_URL}>
                    App Store
                  </a>
                </>
              ) : preferredStore === "ios" ? (
                <>
                  <a className="rp-btn rp-btn--gold" href={IOS_URL}>
                    Download on the App Store
                  </a>
                  <a className="rp-btn rp-btn--ghost" href={ANDROID_URL}>
                    Google Play
                  </a>
                </>
              ) : (
                <>
                  <a className="rp-btn rp-btn--gold" href={IOS_URL}>
                    Download on the App Store
                  </a>
                  <a className="rp-btn rp-btn--ghost" href={ANDROID_URL}>
                    Google Play
                  </a>
                </>
              )}
              <a className="rp-btn rp-btn--ghost" href="/features">
                Range Pocket features
              </a>
            </div>
            <p className="rp-support-note">
              Questions before downloading? Email <a href="mailto:support@armorysync.com">support@armorysync.com</a>.
            </p>
          </div>
          <div className="rp-hero-visual" aria-hidden="true">
            <img src="/features/feature-armory-status.webp" alt="" />
          </div>
          <div className="rp-hero-proof" aria-label="Range Pocket highlights">
            <span>Firearm records</span>
            <span>Maintenance reminders</span>
            <span>Inventory + range logs</span>
          </div>
        </div>
      </section>

      <TrustBand
        className="rp-trust-band"
        eyebrow="Before you download"
        title="Range Pocket is designed as your personal tool for records you actually use."
      />

      <section id="how-we-build-range-pocket" className="rp-build-note" aria-labelledby="rp-build-title">
        <div className="rp-build-copy">
          <div className="rp-section-kicker">How Range Pocket is built</div>
          <h2 id="rp-build-title">Built for range-day records.</h2>
          <p>
            Range Pocket connects the things owners already track: firearms,
            range visits, round counts, maintenance, accessories, photos, and
            training notes.
          </p>
        </div>
        <div className="rp-build-grid">
          <article>
            <strong>Log the visit</strong>
            <span>Range, firearms, rounds, notes, photos, and drills.</span>
          </article>
          <article>
            <strong>Update equipment</strong>
            <span>Accessories, parts, ammo, photos, and upkeep stay attached.</span>
          </article>
          <article>
            <strong>Review next time</strong>
            <span>History stays quick to scan before the next session.</span>
          </article>
        </div>
      </section>

      {/* FEATURES: horizontal carousel */}
      <section
        id="features"
        className="rp-features-h"
        aria-label="Key features"
      >
        <div className="rp-feature-header">
          <h2>Three workflows that make each range trip easier to remember.</h2>
        </div>
        <div className="fade-edge left" aria-hidden="true" />
        <div className="fade-edge right" aria-hidden="true" />

        <div
          className="feature-track"
          ref={featureTrackRef}
          onPointerDown={handleFeaturePointerDown}
          onPointerMove={handleFeaturePointerMove}
          onPointerUp={endFeatureDrag}
          onPointerCancel={endFeatureDrag}
          onPointerLeave={endFeatureDrag}
        >
          {features.map((f, i) => (
            <article className="feature-card" key={i}>
              <button
                className="feature-media"
                onClick={(event) => openFeatureDetail(event, f)}
                aria-label={`Open ${f.title} details`}
              >
                <div className="shot-box">
                  <img src={f.img} alt={f.alt} loading="lazy" />
                </div>
              </button>
              <div className="feature-copy">
                <h3>{f.title}</h3>
                <p>{f.blurb}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="rp-seo-overview" aria-labelledby="rp-overview-title">
        <div className="rp-section-kicker">Firearm Inventory App + Range Log</div>
        <h2 id="rp-overview-title">Everything from your range day, organized in one app.</h2>
        <p>
          Range Pocket is built for shooters who want a cleaner way to track firearm inventory,
          range visits, ammo, accessories, maintenance, training plans, target photos, and notes.
          The app is shaped around practical owner records that are easy to start and useful to review.
        </p>
        <div className="rp-overview-links" aria-label="Range Pocket resources">
          <a href="/firearm-inventory-app">Firearm inventory app</a>
          <a href="/features">Range Pocket features</a>
          <a href="/features/firearm-inventory">Inventory feature</a>
          <a href="/features/range-visit-logging">Range visit logging</a>
          <a href="/range-log-app">Range log app</a>
          <a href="/firearm-maintenance-tracker">Maintenance tracker</a>
          <a href="/blog/range-pocket-complete-tool">Everyday shooter workflow</a>
          <a href="/blog/range-pocket-records">How records work</a>
          <a href="/faq">FAQ</a>
        </div>
      </section>

      <section className="rp-detail-sections" aria-label="Range Pocket capabilities">
        {features.map((feature) => (
          <article className="rp-detail-section" key={feature.title}>
            <div>
              <div className="rp-section-kicker">{feature.title}</div>
              <h2>{feature.modalLead || feature.blurb}</h2>
              <p>{feature.blurb}</p>
            </div>
            {Array.isArray(feature.modalPoints) && feature.modalPoints.length > 0 && (
              <ul>
                {feature.modalPoints.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </section>

      <section className="rp-reading-list" aria-labelledby="rp-reading-title">
        <div>
          <div className="rp-section-kicker">Learn More</div>
          <h2 id="rp-reading-title">Guides for organized range tracking.</h2>
        </div>
        <div className="rp-reading-links">
          <a href="/blog/range-pocket-records">How Range Pocket organizes firearm records</a>
          <a href="/features">Range Pocket features</a>
          <a href="/firearm-inventory-app">Firearm inventory app</a>
          <a href="/range-log-app">Range visit logging</a>
          <a href="/blog/range-time-to-training-habit">Turn range time into a training habit</a>
          <a href="/blog/firearm-maintenance-log-what-to-track">Firearm maintenance log guide</a>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="rp-cta-footer">
        <div className="rp-cta-footer-copy">
          <div className="rp-section-kicker">Range Pocket in use</div>
          <h2>Make the next range trip easier to review.</h2>
          <p>
            Download the app, add the records that matter, and let the next
            range trip build on history you can actually review.
          </p>

          <div className="rp-cta">
            {preferredStore === "android" ? (
              <>
                <a className="rp-btn rp-btn--gold" href={ANDROID_URL}>
                  Get on Google Play
                </a>
                <a className="rp-btn rp-btn--ghost" href={IOS_URL}>
                  App Store
                </a>
              </>
            ) : preferredStore === "ios" ? (
              <>
                <a className="rp-btn rp-btn--gold" href={IOS_URL}>
                  Download on the App Store
                </a>
                <a className="rp-btn rp-btn--ghost" href={ANDROID_URL}>
                  Google Play
                </a>
              </>
            ) : (
              <>
                <a className="rp-btn rp-btn--gold" href={IOS_URL}>
                  Download on the App Store
                </a>
                <a className="rp-btn rp-btn--ghost" href={ANDROID_URL}>
                  Google Play
                </a>
              </>
            )}
          </div>
          <p className="rp-support-note">
            Product questions and setup feedback go to <a href="mailto:support@armorysync.com">support@armorysync.com</a>.
          </p>
        </div>

        <div className="rp-cta-preview" aria-label="Range Pocket preview screens">
          <img src="/features/feature-armory-status.webp" alt="Range Pocket armory status dashboard" />
          <img src="/features/feature-range-visits.webp" alt="Range Pocket range visit list" />
        </div>
      </section>

      <DetailModal open={open} onClose={() => setOpen(false)} item={current} />
    </main>
  );
}

export const RangePocketLanding_Page = {
  path: "/range-pocket",
  element: <RangePocketLanding />,
};
